/**
 * How often a screen that shows the state of the work re-reads it, with nobody pressing anything.
 *
 * ⚠️ **Until 11 October 2026 nothing on this panel moved on its own.** A report filed from the
 * field, a volunteer joining, a mission going to the committee — each appeared when somebody
 * reloaded the page, and the association said so the first week it used it. Every query here was
 * read once and then left for thirty seconds of `staleTime` and a window focus to refresh.
 *
 * ⚠️ **A re-read, not a subscription, and that is the cheaper honest answer here.** The broker
 * publishes a mission's events to that mission's own topic, a wildcard across missions is refused
 * (§8.3), and there is no topic at all for "the board changed" — adding one is an ACL decision
 * (§18.6), not a line of code. A handful of administrators each asking once every fifteen seconds
 * is a few requests a minute; §5.9's objection to polling is to doing it per volunteer for
 * positions, which still arrive over MQTT.
 *
 * ⚠️ Paused in a background tab — TanStack Query's default, relied on deliberately. A panel left
 * open overnight in a tab nobody is looking at asks for nothing.
 */
export const LiveEvery = {
  /** The board, a mission's own page, who holds its control: what an operator is acting on now. */
  work: 15_000,

  /** The bell and the queues somebody is waiting in. */
  queue: 20_000,

  /** Where the volunteers last reported from — the record behind the live feed. */
  positions: 20_000,

  /** PostGIS rebuilds the swept corridor from every point on the search: the slow clock. */
  coverage: 60_000,
} as const;

/** The option every generated hook takes: re-read this often while the tab is being looked at. */
export function liveEvery(milliseconds: number) {
  return { query: { refetchInterval: milliseconds } } as const;
}
